import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { Download, ChevronDown, ChevronUp, User, Calendar, DollarSign, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

// finalized_date is a timestamptz column, not a plain date -- always comes
// back as a full ISO datetime string ("2026-07-29T00:00:00+00:00"), never
// just "2026-07-29". Anything grouping or re-parsing it as a calendar day
// (byDate below, the date-header render) must go through this first, or
// re-appending "T00:00:00" onto an already-full datetime produces a
// malformed string ("...+00:00T00:00:00") that renders as "Invalid Date".
const toCalendarDate = (isoLike) => isoLike ? isoLike.slice(0, 10) : null;

export default function PayrollHistoryTab() {
  const [settlements, setSettlements] = useState([]);
  const [driverNameByEmail, setDriverNameByEmail] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.filter({ status: 'Paid' }, '-finalized_date', 200),
      client.entities.DriverProfile.list('-created_date', 200).catch(() => []),
    ]).then(([data, profiles]) => {
      setSettlements(data);
      const map = {};
      for (const p of profiles) {
        if (p.user_email) map[p.user_email.toLowerCase()] = p.full_name;
      }
      setDriverNameByEmail(map);
      setLoading(false);
    });
  }, []);

  // Group paid settlements by payment_reference + driver, forming "pay runs"
  const payRuns = useMemo(() => {
    // Group by finalized_date + driver_email (each Record Payment call = one run)
    const map = {};
    for (const s of settlements) {
      const finalizedDate = toCalendarDate(s.finalized_date) || s.updated_date?.slice(0, 10);
      const key = `${finalizedDate}_${s.driver_email}_${s.payment_reference || 'nref'}`;
      if (!map[key]) {
        // Prefer the driver's current on-file name over the settlement's
        // creation-time snapshot, which falls back to a raw email whenever
        // the signed-in user had no full_name set at the time (see
        // RecordPaymentModal's pay-statement PDF -- same underlying gap).
        const profileName = driverNameByEmail[s.driver_email?.toLowerCase()];
        const snapshotName = s.driver_name && s.driver_name !== s.driver_email ? s.driver_name : null;
        map[key] = {
          key,
          driver_name: profileName || snapshotName || s.driver_email,
          driver_email: s.driver_email,
          payment_reference: s.payment_reference || '—',
          finalized_date: finalizedDate,
          settlements: [],
          total_gross: 0,
          total_net: 0,
        };
      }
      map[key].settlements.push(s);
      map[key].total_gross += s.gross_revenue || 0;
      map[key].total_net += s.final_net_pay ?? s.net_driver_pay ?? 0;
    }
    return Object.values(map).sort((a, b) => (b.finalized_date || '').localeCompare(a.finalized_date || ''));
  }, [settlements, driverNameByEmail]);

  // Group pay runs by pay date for a timeline view
  const byDate = useMemo(() => {
    const map = {};
    for (const run of payRuns) {
      const date = run.finalized_date || 'Unknown';
      if (!map[date]) map[date] = [];
      map[date].push(run);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [payRuns]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Summary stats
  const totalPaidOut = settlements.reduce((s, x) => s + (x.final_net_pay ?? x.net_driver_pay ?? 0), 0);
  const totalPayRuns = payRuns.length;
  const uniqueDriversPaid = new Set(settlements.map(s => s.driver_email)).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard icon={DollarSign} label="Total Paid Out" value={`$${totalPaidOut.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} color="text-green-400" bg="bg-green-500/10" />
        <StatCard icon={Calendar} label="Pay Runs" value={totalPayRuns} color="text-primary" bg="bg-primary/10" />
        <StatCard icon={User} label="Drivers Paid" value={uniqueDriversPaid} color="text-blue-400" bg="bg-blue-500/10" />
      </div>

      {byDate.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No payroll history yet. Paid settlements will appear here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {byDate.map(([date, runs]) => {
            const dayTotal = runs.reduce((s, r) => s + r.total_net, 0);
            return (
              <div key={date}>
                {/* Date header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="font-space font-semibold text-foreground">
                      {date !== 'Unknown' ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown Date'}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-sm font-semibold text-green-400">${dayTotal.toFixed(2)} total</span>
                </div>

                {/* Driver pay runs for this date */}
                <div className="space-y-3">
                  {runs.map(run => (
                    <div key={run.key} className="glass-card overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors"
                        onClick={() => toggle(run.key)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-foreground">{run.driver_name}</p>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-xs text-muted-foreground">{run.settlements.length} settlement{run.settlements.length !== 1 ? 's' : ''}</span>
                              {run.payment_reference !== '—' && (
                                <span className="text-xs font-mono text-primary/80">Ref: {run.payment_reference}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Net Paid</p>
                            <p className="font-bold text-green-400">${run.total_net.toFixed(2)}</p>
                          </div>
                          {expanded[run.key] ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </button>

                      {expanded[run.key] && (
                        <div className="border-t border-border bg-black/20 divide-y divide-border">
                          {run.settlements.map(s => {
                            // A due-date badge doesn't make sense on an
                            // already-paid record -- instead, flag whether
                            // it was actually paid by its due date, a
                            // simple compliance signal for these rules.
                            // finalized_date is already a full timestamptz --
                            // appending "T23:59:59Z" onto it produced a
                            // malformed string ("...+00:00T23:59:59Z"), so
                            // this comparison was always NaN > date (always
                            // false), silently marking every payment "on
                            // time" regardless of when it actually landed.
                            const wasLate = s.pay_due_at && s.finalized_date
                              && new Date(s.finalized_date) > new Date(s.pay_due_at);
                            return (
                            <div key={s.id} className="flex items-center justify-between px-5 py-3 gap-3">
                              <div>
                                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                                  Load #{s.load_number}
                                  {s.pay_due_at && (
                                    <span className={cn(
                                      "text-[10px] font-semibold px-1.5 py-0.5 rounded-full border",
                                      wasLate ? 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30' : 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30'
                                    )}>
                                      {wasLate ? 'Paid Late' : 'Paid On Time'}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Gross: ${(s.gross_revenue || 0).toFixed(2)} · Net: ${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {s.pdf_url ? (
                                  <>
                                    <a
                                      href={s.pdf_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg text-xs font-semibold hover:bg-primary/20 transition-colors"
                                    >
                                      <FileText className="w-3 h-3" /> View PDF
                                    </a>
                                    <a
                                      href={s.pdf_url}
                                      download={`settlement-${s.load_number}-${s.finalized_date}.pdf`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg text-xs font-semibold hover:bg-green-500/20 transition-colors"
                                    >
                                      <Download className="w-3 h-3" /> Download
                                    </a>
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">No PDF</span>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="glass-card p-4">
      <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", bg)}>
        <Icon className={cn("w-4 h-4", color)} />
      </div>
      <p className="text-xl font-space font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}